import { useQuery } from '@apollo/client';
import { Payments_PaymentProcessorId as PaymentProcessorId } from '__generated__/graphql-types';

import user from 'js/lib/user';

import { useNaptime } from 'bundles/naptimejs';
import PaymentWalletsV1 from 'bundles/naptimejs/resources/paymentWallets.v1';
import type ValidPaymentProcessorsV1 from 'bundles/naptimejs/resources/validPaymentProcessors.v1';
import GetPaymentMethodsByPaymentProcessorIds from 'bundles/payments-common/api/GetPaymentMethodsByPaymentProcessorIds.graphql';
import type {
  GetPaymentMethodsByPaymentProcessorIdsQuery,
  GetPaymentMethodsByPaymentProcessorIdsQueryVariables,
  PaymentMethodDetailsFragment as StripeWalletType,
} from 'bundles/payments-common/api/__generated__/GetPaymentMethodsByPaymentProcessorIds';
import PaymentProcessorTypes from 'bundles/payments-common/common/PaymentProcessorTypes';
import { STRIPE_CONSUMER_PAYMENT_PROCESSOR_IDS } from 'bundles/payments-common/common/constants';

type PropsFromNaptime = {
  wallets: Array<PaymentWalletsV1>;
};

export type PropsFromUseWallets = {
  braintreeWallets: Array<PaymentWalletsV1>;
  paypalWallets: Array<PaymentWalletsV1>;
  stripeWallets: Array<StripeWalletType>;
};

export const useWallets = (
  validPaymentProcessors?: ValidPaymentProcessorsV1,
  skipValidPaymentProcessorCheck?: boolean
) => {
  const isAuthenticated = user.isAuthenticatedUser();
  const shouldFetchStripePaymentMethods = validPaymentProcessors?.isStripeEnabled || skipValidPaymentProcessorCheck;
  const isInvalidPaymentProcessorConfig =
    !validPaymentProcessors ||
    validPaymentProcessors?.isRazorpayEnabled ||
    validPaymentProcessors?.isRazorpayChargeAtWillEnabled ||
    (validPaymentProcessors?.isStripeEnabled && !shouldFetchStripePaymentMethods);

  let stripePaymentProcessorIdList: PaymentProcessorId[] = [
    PaymentProcessorId.StripeConsumer,
    PaymentProcessorId.StripeConsumerSandbox,
  ];
  if (skipValidPaymentProcessorCheck) {
    stripePaymentProcessorIdList = STRIPE_CONSUMER_PAYMENT_PROCESSOR_IDS;
  } else if (validPaymentProcessors?.isStripeBvEnabled) {
    stripePaymentProcessorIdList = [PaymentProcessorId.StripeBvConsumer, PaymentProcessorId.StripeBvConsumerSandbox];
  }

  const {
    data: { wallets },
  } = useNaptime<PropsFromNaptime>(() => {
    // Skip wallets fetch if user is not authenticated
    if (!isAuthenticated) {
      return {};
    }

    // On checkout, we require checking validPaymentProcessors to see if wallets should be fetched
    // In other contexts, like the C+ upgrade modal, we won't check validPaymentProcessors and instead always show wallets
    if (!skipValidPaymentProcessorCheck && isInvalidPaymentProcessorConfig) {
      return {};
    }

    return {
      wallets: PaymentWalletsV1.me([
        'billingCountry',
        'billingZipcode',
        'creditCardExpiration',
        'creditCardLastFourDigits',
        'creditCardType',
        'isValid',
        'email',
        'paymentProcessorId',
      ]),
    };
    // only trigger once when validPaymentProcessors is loaded (use id as a proxy)
  }, [validPaymentProcessors?.id]);

  const { data: stripePaymentMethodsData } = useQuery<
    GetPaymentMethodsByPaymentProcessorIdsQuery,
    GetPaymentMethodsByPaymentProcessorIdsQueryVariables
  >(GetPaymentMethodsByPaymentProcessorIds, {
    variables: {
      paymentProcessorIds: stripePaymentProcessorIdList,
    },
    context: { clientName: 'gatewayGql' },
    skip: !isAuthenticated || !shouldFetchStripePaymentMethods,
  });

  const stripeWallets = stripePaymentMethodsData?.PaymentMethods?.findPaymentMethods?.elements || [];

  if (!wallets) {
    return {
      braintreeWallets: [],
      paypalWallets: [],
      stripeWallets,
    };
  }

  return {
    braintreeWallets: wallets.filter(
      (wallet) => (wallet.isValid && wallet.paymentProcessorId === PaymentProcessorTypes.braintree) ?? false
    ),
    paypalWallets: wallets.filter(
      (wallet) => (wallet.isValid && wallet.paymentProcessorId === PaymentProcessorTypes.paypal) ?? false
    ),
    stripeWallets,
  };
};
